import { useState, useEffect } from 'react'
import { PALETTE } from '../lib/identity.js'
import { formatMeasurement } from '../map/measure.js'

const KIND_ICON = {
  marker: '📍',
  line: '╱',
  polygon: '⬠',
  rectangle: '▭',
  circle: '◯',
}

const KIND_LABEL = {
  marker: 'Marker',
  line: 'Line',
  polygon: 'Polygon',
  rectangle: 'Rectangle',
  circle: 'Circle',
}

function FeatureEditor({ selected, onLabel, onStyle, onDelete, onClose }) {
  const [label, setLabel] = useState(selected.label || '')
  const readOnly = !selected.owned

  // Re-sync only when a DIFFERENT feature is selected. Keying on selected.label
  // too would clobber the user's in-progress typing whenever a remote edit (or
  // our own committed write echoing back) lands.
  useEffect(() => {
    setLabel(selected.label || '')
  }, [selected.id])

  function commitLabel() {
    if (label !== (selected.label || '')) onLabel(selected.id, label)
  }

  const hasFill = selected.kind !== 'marker' && selected.kind !== 'line'

  return (
    <div className="editor">
      <div className="editor-head">
        <strong>{KIND_LABEL[selected.kind] || 'Feature'}</strong>
        <button className="icon-btn" onClick={onClose} title="Close editor">
          ×
        </button>
      </div>

      {readOnly ? (
        // Someone else's feature: show the details, no controls.
        <>
          {label && <div className="readonly-label">{label}</div>}
          {selected.measurement && (
            <div className="measure-readout">{formatMeasurement(selected.measurement)}</div>
          )}
          <div className="editor-foot">
            <span className="editor-meta">
              <span className="dot" style={{ background: selected.authorColor }} /> by{' '}
              {selected.author} — only they can edit it
            </span>
          </div>
        </>
      ) : (
        <>
          <label className="field">
            <span>Label</span>
            <input
              value={label}
              maxLength={60}
              placeholder="Add a label"
              onChange={(e) => setLabel(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitLabel()
                }
              }}
            />
          </label>

          <div className="field">
            <span>Color</span>
            <div className="swatches">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  className={'swatch' + (c === selected.style.color ? ' selected' : '')}
                  style={{ background: c }}
                  onClick={() => onStyle(selected.id, { color: c, fillColor: c })}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>

          {selected.kind !== 'marker' && (
            <label className="field">
              <span>Line weight · {selected.style.weight}</span>
              <input
                type="range"
                min="1"
                max="10"
                value={selected.style.weight}
                onChange={(e) => onStyle(selected.id, { weight: Number(e.target.value) })}
              />
            </label>
          )}

          {hasFill && (
            <label className="field">
              <span>Fill · {Math.round(selected.style.fillOpacity * 100)}%</span>
              <input
                type="range"
                min="0"
                max="0.8"
                step="0.05"
                value={selected.style.fillOpacity}
                onChange={(e) => onStyle(selected.id, { fillOpacity: Number(e.target.value) })}
              />
            </label>
          )}

          {selected.measurement && (
            <div className="measure-readout">{formatMeasurement(selected.measurement)}</div>
          )}

          <div className="editor-foot">
            <span className="editor-meta">by {selected.author}</span>
            <button className="btn-danger" onClick={() => onDelete(selected.id)}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// Right-hand panel: the editor for the selected feature (if any), plus the full
// feature list.
export default function FeaturePanel({ selected, features, onSelect, onLabel, onStyle, onDelete }) {
  return (
    <div className="panel feature-panel">
      {selected && (
        <FeatureEditor
          key={selected.id}
          selected={selected}
          onLabel={onLabel}
          onStyle={onStyle}
          onDelete={onDelete}
          onClose={() => onSelect(null)}
        />
      )}

      <div className="panel-title">Features · {features.length}</div>
      <ul className="feature-list">
        {features.map((f) => (
          <li
            key={f.id}
            className={selected?.id === f.id ? 'active' : ''}
            onClick={() => onSelect(f.id)}
          >
            <span className="dot" style={{ background: f.authorColor }} />
            <span className="f-kind">{KIND_ICON[f.kind] || '•'}</span>
            <span className="f-label">{f.label || KIND_LABEL[f.kind] || f.kind}</span>
            {f.measurement && (
              <span className="f-measure">{formatMeasurement(f.measurement)}</span>
            )}
          </li>
        ))}
        {features.length === 0 && (
          <li className="empty">Nothing here yet — draw with the toolbar (top-left).</li>
        )}
      </ul>
    </div>
  )
}
